import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";

import apiClient from "../services/apiClient";

import {
  register as apiRegister,
  sendOTP as apiSendOtp,
  verifyOTP as apiVerifyOtp,
  logout as logoutService,
} from "../services/authService";

/* ===================================================== */
/* CONTEXT                                               */
/* ===================================================== */

const AuthContext =
  createContext(null);

/* ===================================================== */
/* PROVIDER                                              */
/* ===================================================== */

export const AuthProvider = ({
  children,
}) => {

  /* =================================================== */
  /* STATE                                               */
  /* =================================================== */

  const [token, setToken] =
    useState(() =>
      localStorage.getItem(
        "token"
      )
    );

  const [
    companyId,
    setCompanyId,
  ] = useState(() =>
    localStorage.getItem(
      "companyId"
    )
  );

  const [user, setUser] =
    useState(null);

  const [
    membership,
    setMembership,
  ] = useState(null);

  const [
    companies,
    setCompanies,
  ] = useState([]);

  const [loading, setLoading] =
    useState(true);

  const [
  authInitialized,
  setAuthInitialized,
  ] = useState(false);

  const [
    subscriptionRefreshing,
    setSubscriptionRefreshing,
  ] = useState(false);

  const isLoggedIn =
    Boolean(token);

  /* =================================================== */
  /* SESSION STORAGE SYNC                               */
  /* =================================================== */

  useEffect(() => {

    if (token) {
      localStorage.setItem(
        "token",
        token
      );
    } else {
      localStorage.removeItem(
        "token"
      );
    }

  }, [token]);

  useEffect(() => {

    if (companyId) {
      localStorage.setItem(
        "companyId",
        companyId
      );
    } else {
      localStorage.removeItem(
        "companyId"
      );
    }

  }, [companyId]);

  /* =================================================== */
  /* API HEADER SYNC                                    */
  /* =================================================== */

  useEffect(() => {

    if (token) {

      apiClient.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${token}`;

    } else {

      delete apiClient.defaults
        .headers.common[
          "Authorization"
        ];

    }

  }, [token]);

  useEffect(() => {

    if (companyId) {

      apiClient.defaults.headers.common[
        "x-company-id"
      ] = companyId;

    } else {

      delete apiClient.defaults
        .headers.common[
          "x-company-id"
        ];

    }

  }, [companyId]);

  /* =================================================== */
  /* CLEAR SESSION                                      */
  /* =================================================== */

  const clearSession =
    useCallback(() => {

      setToken(null);

      setCompanyId(null);

      setUser(null);

      setMembership(null);

      setCompanies([]);

      localStorage.removeItem(
        "token"
      );

      localStorage.removeItem(
        "companyId"
      );

    }, []);

  /* =================================================== */
  /* LOAD PROFILE                                       */
  /* =================================================== */

const loadProfile =
  useCallback(async () => {

    try {

      const { data } =
        await apiClient.get(
          "/user/profile"
        );

      const normalizedUser =
        data?.user || data;

      const normalizedMembership =
        data?.membership ||
        normalizedUser?.membership ||
        null;

      setUser(
        normalizedUser
      );

      setMembership(
        normalizedMembership
      );

      return {
        user:
          normalizedUser,
        membership:
          normalizedMembership,
      };

    } catch (err) {

      console.warn(
        "[Auth] Profile load failed:",
        err?.message
      );

      clearSession();

      return null;

    }

  }, [clearSession]);

  /* =================================================== */
  /* LOAD COMPANIES                                     */
  /* =================================================== */

  const loadCompanies =
    useCallback(async () => {

      try {

        const { data } =
          await apiClient.get(
            "/company"
          );

        const list =
          Array.isArray(data)
            ? data
            : [];

        setCompanies(list);

        setCompanyId((prev) => {

          if (
            prev &&
            list.some(
              (c) =>
                c._id === prev
            )
          ) {
            return prev;
          }

          return (
            list?.[0]?._id ||
            null
          );

        });

        return list;

      } catch (err) {

        console.warn(
          "[Auth] Companies load failed:",
          err.message
        );

        setCompanies([]);

        return [];

      }

    }, []);

  /* =================================================== */
  /* REFRESH COMPANY                                    */
  /* =================================================== */

const refreshCompany =
  useCallback(
    async (
      targetCompanyId = null
    ) => {

      try {

        const currentId =
          targetCompanyId ||
          companyId;

        if (!currentId) {
          return null;
        }

        const { data } =
          await apiClient.get(
            `/company/${currentId}`
          );

        const company =
          data?.company ||
          data;

        if (!company?._id) {
          return null;
        }

        setCompanies((prev) => {

          const exists =
            prev.some(
              (c) =>
                c._id ===
                company._id
            );

          if (!exists) {

            return [
              ...prev,
              company,
            ];

          }

          return prev.map(
            (existingCompany) =>
              existingCompany._id ===
              company._id
                ? company
                : existingCompany
          );

        });

        return company;

      } catch (err) {

        console.warn(
          "[Auth] refreshCompany failed:",
          err?.message
        );

        return null;

      }

    },
    [companyId]
  );

  /* =================================================== */
  /* POLL PLAN ACTIVATION                               */
  /* =================================================== */

  const pollForPlanActivation =
    useCallback(
      async ({
        expectedPlan,
        timeout = 45000,
        interval = 2000,
      }) => {

        if (!expectedPlan) {
          return {
            success: false,
            reason:
              "Expected plan missing",
          };
        }

        setSubscriptionRefreshing(
          true
        );

        try {

          const startedAt =
            Date.now();

          while (
            Date.now() -
              startedAt <
            timeout
          ) {

            const company =
              await refreshCompany();

            if (
              company?.plan ===
              expectedPlan
            ) {

              await Promise.all([
                loadProfile(),
                loadCompanies(),
              ]);

              return {
                success: true,
                company,
              };

            }

            await new Promise(
              (resolve) =>
                setTimeout(
                  resolve,
                  interval
                )
            );

          }

          return {
            success: false,
            reason:
              "Activation timeout",
          };

        } catch (err) {

          return {
            success: false,
            reason:
              err.message,
          };

        } finally {

          setSubscriptionRefreshing(
            false
          );

        }

      },
      [
        refreshCompany,
        loadProfile,
        loadCompanies,
      ]
    );

  /* =================================================== */
  /* INITIAL BOOT                                       */
  /* =================================================== */

    useEffect(() => {

  let mounted = true;

  const bootstrap =
    async () => {

      if (!token) {

        if (mounted) {

          setLoading(false);

          setAuthInitialized(
            true
          );

        }

        return;

      }

      try {

        /* -------------------------------- */
        /* STEP 1: PROFILE FIRST            */
        /* -------------------------------- */

        await loadProfile();

        /* -------------------------------- */
        /* STEP 2: COMPANIES                */
        /* -------------------------------- */

        await loadCompanies();

      } catch (err) {

        console.error(
          "[Auth] Bootstrap failed:",
          err
        );

      } finally {

        if (mounted) {

          setLoading(false);

          setAuthInitialized(
            true
          );

        }

      }

    };

    bootstrap();

    return () => {

      mounted = false;

    };

  }, [
    token,
    loadProfile,
    loadCompanies,
  ]);

  /* =================================================== */
  /* LOGIN                                               */
  /* =================================================== */

  const loginWithToken =
    useCallback(
      (newToken) => {

        if (!newToken) {
          return;
        }

        setToken(newToken);

      },
      []
    );

  /* =================================================== */
  /* REGISTER                                            */
  /* =================================================== */

 const register =
  useCallback(
    async (
      name,
      email
    ) => {

      const data =
        await apiRegister(
          name,
          email
        );

      if (data?.token) {

        loginWithToken(
          data.token
        );

          const companies =
            await loadCompanies();

          const resolvedCompanyId =
            companies?.[0]?._id;

          if (resolvedCompanyId) {

            apiClient.defaults.headers.common[
              "x-company-id"
            ] = resolvedCompanyId;

          }

          await loadProfile();

      }

      return data;

    },
    [
      loginWithToken,
      loadProfile,
      loadCompanies,
    ]
  );
  /* =================================================== */
  /* SEND OTP                                            */
  /* =================================================== */

    const sendOtp =
      useCallback(
        async (email) => {

          return apiSendOtp({
            email,
          });

        },
        []
      );

  /* =================================================== */
  /* VERIFY OTP                                          */
  /* =================================================== */
const verifyOtp =
  useCallback(
    async (
      email,
      otp
    ) => {

      const data =
        await apiVerifyOtp({
          email,
          otp,
        });

      if (!data?.token) {

        throw new Error(
          "Authentication failed"
        );

      }

      loginWithToken(
        data.token
      );

      const companies =
        await loadCompanies();

      const resolvedCompanyId =
        companies?.[0]?._id;

      if (resolvedCompanyId) {

        apiClient.defaults.headers.common[
          "x-company-id"
        ] = resolvedCompanyId;

      }

      await loadProfile();

      return data;

    },
    [
      loginWithToken,
      loadProfile,
      loadCompanies,
    ]
  );
  /* =================================================== */
  /* LOGOUT                                              */
  /* =================================================== */

  const logout =
  useCallback(
    async () => {
      try {
        await logoutService();
      } catch (err) {
        console.warn(
          "[Auth] Logout failed:",
          err?.message
        );
      }
      clearSession();
    },
    [clearSession]
  );

  /* =================================================== */
  /* COMPANY SWITCH                                      */
  /* =================================================== */

  const selectCompany =
    useCallback((id) => {

      if (!id) {
        return;
      }

      setCompanyId(id);

    }, []);

  /* =================================================== */
  /* DERIVED                                             */
  /* =================================================== */

  const activeCompany =
    useMemo(() => {

      return (
        companies.find(
          (c) =>
            c._id ===
            companyId
        ) || null
      );

    }, [
      companies,
      companyId,
    ]);

    const isAdmin =
      Boolean(
        membership?.role ===
          "admin" ||

        activeCompany?.members?.some(
          (member) =>
            String(member?.user) ===
              String(user?._id) &&
            member?.role ===
              "admin"
        )
      );
  /* =================================================== */
  /* CONTEXT VALUE                                       */
  /* =================================================== */
const value = useMemo(
  () => ({
    /* ================================================= */
    /* SESSION                                           */
    /* ================================================= */

    token,
    companyId,

    /* ================================================= */
    /* USER                                              */
    /* ================================================= */

    user,
    membership,

    /* ================================================= */
    /* COMPANIES                                         */
    /* ================================================= */

    companies,
    activeCompany,

    /* ================================================= */
    /* STATE                                             */
    /* ================================================= */

    loading,

    authInitialized,

    isLoggedIn,

    isAdmin,

    subscriptionRefreshing,

    /* ================================================= */
    /* AUTH                                              */
    /* ================================================= */

    register,

    sendOtp,

    verifyOtp,

    logout,

    login:
      loginWithToken,

    /* ================================================= */
    /* COMPANY                                           */
    /* ================================================= */

    selectCompany,

    refreshCompany,

    refreshCompanies:
      loadCompanies,

    /* ================================================= */
    /* PROFILE                                           */
    /* ================================================= */

    refreshProfile:
      loadProfile,

    /* ================================================= */
    /* BILLING                                           */
    /* ================================================= */

    pollForPlanActivation,
  }),
  [
    /* session */
    token,
    companyId,

    /* user */
    user,
    membership,

    /* companies */
    companies,
    activeCompany,

    /* state */
    loading,
    authInitialized,
    isLoggedIn,
    isAdmin,
    subscriptionRefreshing,

    /* auth */
    register,
    sendOtp,
    verifyOtp,
    logout,
    loginWithToken,

    /* company */
    selectCompany,
    refreshCompany,
    loadCompanies,

    /* profile */
    loadProfile,

    /* billing */
    pollForPlanActivation,
  ]
);

  /* =================================================== */
  /* RENDER                                              */
  /* =================================================== */
if (
  loading ||
  !authInitialized
) {
  return (
    <div className="p-8 text-center text-gray-500">
      Loading...
    </div>
  );
}

return (
  <AuthContext.Provider
    value={value}
  >
    {children}
  </AuthContext.Provider>
);
};

/* ===================================================== */
/* HOOK                                                  */
/* ===================================================== */

export const useAuth = () => {

  const ctx =
    useContext(
      AuthContext
    );

  if (!ctx) {
    throw new Error(
      "useAuth must be used inside AuthProvider"
    );
  }

  return ctx;

};